import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrowLeft,
  Printer,
  Download,
  MessageCircle,
  Copy,
  Check,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  downloadTicketPdf,
  downloadAllTicketsPdf,
  whatsappShareUrl,
} from "@/lib/ticketDistribution";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const publicTicketUrl = (code) => `${window.location.origin}/t/${code}`;

export default function TicketsSheet() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [copiedId, setCopiedId] = useState("");

  useEffect(() => {
    Promise.all([api.get(`/events/${id}`), api.get(`/events/${id}/tickets`)])
      .then(([ev, tk]) => {
        setEvent(ev.data);
        setTickets(tk.data);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleDownloadAll = async () => {
    if (!event || tickets.length === 0) return;
    setBulkBusy(true);
    try {
      await downloadAllTicketsPdf({
        event,
        tickets,
        makePublicUrl: publicTicketUrl,
      });
    } finally {
      setBulkBusy(false);
    }
  };

  const handleCopyLink = async (t) => {
    try {
      await navigator.clipboard.writeText(publicTicketUrl(t.code));
      setCopiedId(t.id);
      setTimeout(() => setCopiedId(""), 1500);
    } catch {
      /* ignore */
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5]">
        <div className="brut-spinner" />
      </div>
    );
  }
  if (!event) return null;

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="tickets-sheet">
      {/* Toolbar (hidden on print) */}
      <div className="no-print sticky top-0 bg-white border-b-2 border-[#121212] z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <Link
            to={`/admin/events/${id}`}
            className="inline-flex items-center gap-2 mono-label hover:text-[#002FA7]"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to event
          </Link>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <span className="mono-label text-[#666] hidden sm:inline">
              {tickets.length} TICKETS
            </span>
            <button
              onClick={handleDownloadAll}
              disabled={bulkBusy}
              className="brut-btn-secondary inline-flex items-center gap-2"
              data-testid="download-all-pdf-btn"
            >
              {bulkBusy ? (
                <div className="brut-spinner !w-4 !h-4 !border-[#121212]" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Download All PDF
            </button>
            <button
              onClick={() => window.print()}
              className="brut-btn-accent inline-flex items-center gap-2"
              data-testid="print-btn"
            >
              <Printer className="w-4 h-4" /> Print
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="no-print mb-6">
          <h1 className="font-display text-3xl font-black uppercase tracking-tighter">
            {event.name} — Ticket Sheet
          </h1>
          <p className="text-sm text-[#666] mt-1 font-mono">
            Each ticket has a unique QR. Download a PDF, share via WhatsApp, or
            copy the public link to send anywhere.
          </p>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          data-testid="tickets-grid"
        >
          {tickets.map((t) => {
            const publicUrl = publicTicketUrl(t.code);
            const waUrl = whatsappShareUrl({
              event,
              ticket: t,
              publicUrl,
            });
            const isCopied = copiedId === t.id;
            return (
              <div
                key={t.id}
                className="ticket-print bg-white border-2 border-[#121212] flex flex-col"
                data-testid={`ticket-${t.serial}`}
              >
                <div className="flex">
                  {/* Stub */}
                  <div className="bg-[#121212] text-white p-3 flex flex-col justify-between min-w-[80px] border-r-2 border-[#121212]">
                    <div className="mono-label text-[#FFCC00]">// TKT</div>
                    <div className="font-display text-3xl font-black tracking-tighter">
                      #{t.serial}
                    </div>
                    <div className="mono-label text-[10px] text-[#A1A1AA]">
                      {t.code.slice(0, 6)}
                    </div>
                  </div>
                  {/* Body */}
                  <div className="flex-1 p-3 flex items-center gap-3">
                    <div className="bg-white p-1 border-2 border-[#121212]">
                      <QRCodeSVG value={t.code} size={88} level="M" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-display text-sm font-black uppercase truncate">
                        {event.name}
                      </div>
                      <div className="text-[10px] font-mono text-[#666] mt-1 truncate">
                        {event.venue}
                      </div>
                      <div className="text-[10px] font-mono text-[#666]">
                        {formatDate(event.start_at)}
                      </div>
                      <div className="font-mono text-[11px] mt-1 break-all">
                        {t.code}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions (hidden on print) */}
                <div
                  className="no-print border-t-2 border-[#121212] grid grid-cols-3 divide-x-2 divide-[#121212]"
                  data-testid={`ticket-actions-${t.serial}`}
                >
                  <button
                    onClick={() =>
                      downloadTicketPdf({ event, ticket: t, publicUrl })
                    }
                    className="py-2 px-1 mono-label hover:bg-[#F4F4F5] flex items-center justify-center gap-1"
                    data-testid={`ticket-pdf-${t.serial}`}
                    title="Download PDF"
                  >
                    <Download className="w-3 h-3" /> PDF
                  </button>
                  <a
                    href={waUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="py-2 px-1 mono-label hover:bg-[#FFCC00] flex items-center justify-center gap-1 no-underline text-[#121212]"
                    data-testid={`ticket-whatsapp-${t.serial}`}
                    title="Share to WhatsApp"
                  >
                    <MessageCircle className="w-3 h-3" /> WHATS
                  </a>
                  <button
                    onClick={() => handleCopyLink(t)}
                    className="py-2 px-1 mono-label hover:bg-[#F4F4F5] flex items-center justify-center gap-1"
                    data-testid={`ticket-copy-${t.serial}`}
                    title="Copy public link"
                  >
                    {isCopied ? (
                      <>
                        <Check className="w-3 h-3" /> COPIED
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" /> LINK
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
